import { useState, useRef, useCallback, useEffect } from 'react'
import { toast } from 'sonner'
import { apiClient, useAuthStore } from '@indipia/shared'
import {
  ShareHistoryScreen,
  ReadBadge,
  ShareStateBadge,
  type RecipientType,
  type ShareHistoryRow,
  type ShareHistoryRecipient as PubRecipient,
  type ShareState,
  type ShareTypeOption,
  type FetchSharePage,
} from '@indipia/shared-features'
import type { NavFn } from '../pages/SchedulerPage'
import { scheduleApi } from '../api/scheduleApi'
import {
  fetchRevokeImpact,
  resendScheduleShareHistory,
  apiErrorInfo,
  SHARE_SEND_WINDOW_CLOSED,
  SEND_WINDOW_CLOSED_NOTICE,
  type RevokeImpact,
} from '../api/scheduleShareApi'
import { useShareSendableMap } from '../hooks/useShareSendable'
import { KakaoMessageCard } from './KakaoTemplatePreview'
import { foremanSendErrorMessage } from '../constants/foremanSendMessage'
import { getResponseState, RESPONSE_BADGE, type ResponseState } from '../constants/partnerStatus'
import { Button, Badge, Modal, StatusHint, Tooltip } from '@indipia/ui-core'

type Props = {
  navigate: NavFn
}

// ── /schedule/schedules/share-histories 원본 응답 레코드 ──────────
interface RawShareHistory {
  historyId:       number
  scheduleId:      number
  siteName:        string | null
  dispatchType:    string           // 'FOREMAN_CONFIRM' | 'SCHEDULE_SHARE' ...
  channelType:     string | null
  taskId:          number | null
  shareId:         number | null
  shareBatchId:    string | null
  dispatchBatchId: string | null
  recipientId:     number | null
  recipientName:   string | null
  recipientPhone:  string | null
  recipientType:   string | null    // 'PARTNER' | 'CUSTOMER' | 'VENDOR' ...
  shareUrl:        string | null
  expiresDtm:      string | null
  msgTitle:        string | null
  msgContent:      string | null
  sentDtm:          string | null
  viewedDtm:       string | null
  revokedYn:       string | null
  revokedDtm:      string | null
  /** 공종의 현재 반장 상태 코드(E01_TASK_PARTNER_STATUS) — FOREMAN_CONFIRM 판정용 */
  partnerStatus:   string | null
  /** 공종의 반장 회신 일시 */
  respondedDtm:    string | null
  /** 마감 사유 — EXPIRED·REPLACED·CHANGED, 마감 아니면 null */
  closedReason?:   string | null
  /** 공종의 현재 반장 번호 — 반장 없으면 없음/null */
  taskPartnerId?:  number | null
  /** 이 이력 행의 회신 결과(CONFIRMED·DECLINED), 미회신이면 null — API-013 */
  responseResult?: string | null
}

// ── 화면에서 다루는 그룹 단위(같은 발송 배치 = 한 행) ──────────────
type ShareStatus = 'read' | 'unread'

/** FOREMAN_CONFIRM 전용 반장 일정확인 상태 — SCHEDULE_SHARE 등 다른 구분에서는 null.
 *  partnerStatus 한 필드로 판정(constants/partnerStatus#getResponseState):
 *  WAITING=pending · CONFIRMED=confirmed · DECLINED=declined · UNFIXED=unfixed · 그 외=idle. */
type ConfirmState = 'confirmed' | 'declined' | 'pending' | 'unfixed' | 'idle'

/** 반장이 가능/불가 중 하나로 회신한 상태인지 */
function isResponded(c: ConfirmState): boolean {
  return c === 'confirmed' || c === 'declined'
}

const RESPONSE_TO_CONFIRM: Record<ResponseState, ConfirmState> = {
  ok: 'confirmed', no: 'declined', wait: 'pending', unfixed: 'unfixed', idle: 'idle',
}

interface ShareHistoryRecipient {
  name:          string
  role:          string
  tel:           string
  /** 원본 코드값('CUSTOMER'|'PARTNER'|'VENDOR') — 재발송 시 발송 페이로드에 필요 */
  recipientType: RecipientType
  status:        ShareStatus
  respAt:        string | null
  confirmState:  ConfirmState | null
  /** FOREMAN_CONFIRM 재발송 시 버튼 링크(sched:{taskId}:confirmed|unconfirmed) 구성에 필요.
   *  같은 dispatchBatchId 안에서도 건별로 taskId가 다를 수 있어(일괄발송) 그룹이 아닌 건 단위로 둔다. */
  taskId:        number | null
  /** 이 수신인에게 실제 발송됐던 본문 — 그룹 대표 content(row.content)가 아니라 건별 원본(서버가 접수 시 저장한 값) */
  content:       string
  /** 이력 행 번호 — 행 키·공유취소·재발송 대상 식별에 쓴다(그룹(batch) 안에서도 수신인마다 다르다) */
  historyId:     number
  revokedYn:     string
  /** 이 건에 적용된 링크 유효기간 — 지났으면 버튼을 누르지 않아도 만료된 것으로 표시한다 */
  expiresDtm:    string | null
  /** 마감 사유(EXPIRED·REPLACED·CHANGED), 마감 아니면 null — API-013 */
  closedReason:  string | null
  /** 수신인(반장) 번호 — FOREMAN_CONFIRM 재발송의 partnerId. 과거 행은 null */
  recipientId:   number | null
  /** 공종의 현재 반장 번호(API-013) — 없으면 null(구서버·반장 없음) */
  taskPartnerId: number | null
  /** 발송 매체(카카오/문자/메일) — 공용 목록 '매체' 열 표시용. 미확정이면 null */
  channelType:   string | null
  /** 공유 링크 PK(API-013) — 공용 수신자 모델 shareId */
  shareId:       number | null
}

interface ShareHistoryGroup {
  groupKey:     string
  dispatchType: string
  scheduleId:   number
  siteName:     string
  content:      string
  sentDtm:       string | null
  /** 최초 발송 시 적용된 링크 유효기간 */
  expiresDtm:   string | null
  recipients:   ShareHistoryRecipient[]
}

const ROLE_LABEL: Record<string, string> = { PARTNER: '시공반장', CUSTOMER: '고객', VENDOR: '거래처' }
// 카테고리(정성적) 색상 팔레트 — 수신인 역할 구분용, 토큰 흡수 대상 아님 (예외 유지)
const ROLE_COLOR: Record<string, string> = { '고객': '#9B4DCA', '시공반장': '#C47850', '거래처': '#1471A8' }

// 다른 메뉴 공유 건수 표시용 라벨 — 서버 otherMenus[].menu 코드가 여기 없으면 코드 그대로 보여준다.
const SHARE_MENU_LABEL: Record<string, string> = {
  SCHEDULE: '스케줄 공유', PHOTO: '사진 공유', WORKLOG: '공사현황 공유', WORK_LOG: '공사현황 공유',
}

// 발송 구분(FOREMAN_CONFIRM/SCHEDULE_SHARE) 2종 — 공용 조회카드/목록 '구분' 필터·배지.
const TYPE_OPTIONS: ShareTypeOption[] = [
  { value: 'FOREMAN_CONFIRM', label: '시공반장 전송', badgeStatus: 'info' },
  { value: 'SCHEDULE_SHARE',  label: '일정공유',     badgeStatus: 'warning' },
]

function pad(n: number) { return n < 10 ? '0' + n : String(n) }
function fmtDateTime(s: string | null) {
  if (!s) return '-'
  const d = new Date(s)
  if (isNaN(d.getTime())) return '-'
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** 서버가 준 만료 시각('YYYY-MM-DDT…')을 M/D 로 표기 — 날짜 계산 없이 문자열만 나눈다. */
function monthDay(s: string | null): string {
  if (!s) return ''
  const [, m, d] = s.slice(0, 10).split('-')
  return m && d ? `${Number(m)}/${Number(d)}` : ''
}

/** 공용 질의 dateFrom/dateTo(YYYYMMDD) → BE sentFrom/sentTo(YYYY-MM-DD) */
function ymdToDash(ymd: string): string {
  return `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}`
}

/** 고객 대시보드 발송자 미리보기(새 창) — 로그인 토큰으로 열며 열람 기록·열람 알림이 남지 않는다.
 *  historyId 는 과거 발송 건의 본문·기준으로 열도록 서버에 넘긴다. */
function buildCustomerPreviewUrl(shareId: number, historyId: number): string {
  return `${window.location.origin}/customer-home/preview?shareId=${shareId}&historyId=${historyId}`
}

function groupHistories(raw: RawShareHistory[]): ShareHistoryGroup[] {
  const map = new Map<string, ShareHistoryGroup>()
  raw.forEach(r => {
    const key = r.dispatchBatchId ?? r.shareBatchId ?? String(r.historyId)
    const role = ROLE_LABEL[r.recipientType ?? ''] ?? (r.recipientType ?? '수신인')
    const isKnownType = r.recipientType === 'CUSTOMER' || r.recipientType === 'PARTNER' || r.recipientType === 'VENDOR'
    const isForemanConfirm = r.dispatchType === 'FOREMAN_CONFIRM'
    // 이 행의 수신인이 지금 공종의 반장인지 — 응답의 partnerStatus·respondedDtm 은 공종(현재 반장) 값이다.
    const isCurrentPartner = r.recipientId != null && r.taskPartnerId != null
      && Number(r.recipientId) === Number(r.taskPartnerId)
    // FR-C-141: 이 행에 기록된 회신 결과(responseResult)를 우선한다. 행에 결과가 없을 때만
    // 현재 반장의 행에 한해 공종의 반장 상태로 보충하고, 이전 반장의 행은 새 반장의 상태를 따르지 않는다.
    const confirmState: ConfirmState | null = !isForemanConfirm ? null
      : r.responseResult === 'CONFIRMED' ? 'confirmed'
      : r.responseResult === 'DECLINED'  ? 'declined'
      : isCurrentPartner                 ? RESPONSE_TO_CONFIRM[getResponseState({ partnerStatus: r.partnerStatus })]
      : 'idle'
    // 회신 시각: 응답의 respondedDtm 은 공종의 현재 반장 값이라 현재 반장의 행에만 쓴다(이전 반장 행의 시각은 응답에 없음).
    const foremanRespAt = isCurrentPartner ? r.respondedDtm : null
    const recipient: ShareHistoryRecipient = {
      name:          r.recipientName ?? '알 수 없음',
      role,
      tel:           r.recipientPhone ?? '',
      recipientType: isKnownType ? (r.recipientType as RecipientType) : 'CUSTOMER',
      status:        r.viewedDtm ? 'read' : 'unread',
      // FOREMAN_CONFIRM은 "응답시각"(respondedDtm)이 의미상 respAt — 페이지 열람
      // 시각(viewedDtm)과는 다른 완료 신호다.
      respAt:        isForemanConfirm ? foremanRespAt : r.viewedDtm,
      confirmState,
      taskId:        r.taskId,
      // 서버가 접수 시 저장한 발송 본문 — 화면이 다시 조립하지 않는다. 본문이 없으면 제목.
      content:       r.msgContent || r.msgTitle || '(내용 없음)',
      historyId:     r.historyId,
      revokedYn:     r.revokedYn ?? 'N',
      expiresDtm:    r.expiresDtm,
      closedReason:  r.closedReason ?? null,
      recipientId:   r.recipientId ?? null,
      taskPartnerId: r.taskPartnerId ?? null,
      channelType:   r.channelType ?? null,
      shareId:       r.shareId ?? null,
    }
    const existing = map.get(key)
    if (existing) {
      existing.recipients.push(recipient)
      if (r.sentDtm && (!existing.sentDtm || r.sentDtm < existing.sentDtm)) existing.sentDtm = r.sentDtm
    } else {
      map.set(key, {
        groupKey:     key,
        dispatchType: r.dispatchType,
        scheduleId:   r.scheduleId,
        siteName:     r.siteName || '—',
        content:      r.msgTitle || r.msgContent || '(내용 없음)',
        sentDtm:       r.sentDtm,
        expiresDtm:   r.expiresDtm,
        recipients:   [recipient],
      })
    }
  })
  return Array.from(map.values())
}

// FOREMAN_CONFIRM은 "확정"(일정가능)이 아니면 재발송 대상 — 일정불가능(declined)도 SigongPopup의
// 재전송 UX와 동일하게 다시 보낼 수 있어야 하고, 미응답(pending)은 당연히 대상이다.
// 그 외(SCHEDULE_SHARE 등)는 기존과 동일하게 미열람 여부로 판단한다.
// 공유취소(회수)된 건은 재발송 대상에서 제외한다 — 공유취소 버튼을 누른 건에 재발송 버튼이 다시 뜨면 안 된다.
// 대체(REPLACED)·변경(CHANGED)으로 마감된 FOREMAN_CONFIRM 건은 재발송 대상이 아니다(서버는 공종의 현재 반장에게
// 보내므로 이전 반장 행에서 눌러도 다른 사람에게 간다). EXPIRED(기한 경과)는 현재 반장에게 재발송이 필요할 수 있어 유지한다.
function canResend(r: ShareHistoryRecipient): boolean {
  if (r.revokedYn === 'Y') return false
  // 수신인 번호를 모르는 과거 FOREMAN_CONFIRM 행은 다시 보낼 대상을 특정할 수 없어 제외한다.
  if (r.confirmState != null && r.recipientId == null) return false
  if (r.confirmState != null && (r.closedReason === 'REPLACED' || r.closedReason === 'CHANGED')) return false
  // 수신인이 공종의 현재 반장이 아니거나(반장이 바뀜·기한 후 다음 순위로 자동 전환) 현재 반장이 없으면 재발송 대상이 아니다.
  if (r.confirmState != null && (r.taskPartnerId == null || Number(r.recipientId) !== Number(r.taskPartnerId))) return false
  return r.confirmState != null ? r.confirmState !== 'confirmed' : r.status === 'unread'
}

// 링크의 유효기간(서버가 준 expiresDtm)이 이미 지났으면, 버튼을 눌러 회수하지 않았어도 사실상
// 만료된 것으로 본다 — 공유취소 버튼 대신 '만료' 배지로 표시한다.
function isPastExpiry(expiresDtm: string | null): boolean {
  if (!expiresDtm) return false
  const t = new Date(expiresDtm).getTime()
  return !isNaN(t) && t < Date.now()
}

/** 링크 상태 — 취소(회수) / 만료(유효기간 경과) / 정상 */
function shareStateOf(r: ShareHistoryRecipient): ShareState {
  if (r.revokedYn === 'Y') return 'REVOKED'
  return isPastExpiry(r.expiresDtm) ? 'EXPIRED' : 'NORMAL'
}

// 공유취소(회수)는 이미 회수됐거나, 유효기간이 이미 지났거나, FOREMAN_CONFIRM인데 이미
// 응답(가능/불가능)이 온 건이면 숨긴다 — 이미 답한 반장에게 보낸 링크를 만료 처리할 이유가
// 없다. 열람 여부(status==='read')는 더 이상 막지 않는다 — 이미 열람한 링크도 취소할 수 있어야 한다.
function canRevoke(r: ShareHistoryRecipient): boolean {
  if (r.revokedYn === 'Y') return false
  if (isPastExpiry(r.expiresDtm)) return false
  if (r.confirmState != null && r.confirmState !== 'pending') return false // WAITING(회신 대기)만 회수 가능
  return true
}

// 수신인이 "열람/응답 완료"인지 — FOREMAN_CONFIRM은 회신(가능/불가) 여부, 그 외는 열람 여부.
// 공용 목록의 '열람' 배지(ReadCell)와 조회카드 열람상태 필터가 같은 기준을 쓰도록 한 곳에서 판정한다.
function recipientViewed(r: ShareHistoryRecipient): boolean {
  return r.confirmState ? isResponded(r.confirmState) : r.status === 'read'
}

const CONFIRM_BADGE: Record<ConfirmState, (typeof RESPONSE_BADGE)[ResponseState]> = {
  confirmed: RESPONSE_BADGE.ok,
  declined:  RESPONSE_BADGE.no,
  pending:   RESPONSE_BADGE.wait,
  unfixed:   RESPONSE_BADGE.unfixed,
  idle:      RESPONSE_BADGE.idle,
}

// FOREMAN_CONFIRM 행은 반장의 실제 일정확인 상태(confirmState)를, 그 외는 공용 열람/미열람
// 배지를 보여준다 — recipient 하나로 두 구분을 함께 렌더링하기 위한 스위치. ui-core Badge 공용 사용.
function StatusBadge({ rec }: { rec: ShareHistoryRecipient }) {
  if (rec.confirmState) {
    const b = CONFIRM_BADGE[rec.confirmState]
    return <Badge status={b.status} className="whitespace-nowrap">{b.label}</Badge>
  }
  return <ReadBadge viewed={rec.status === 'read'} />
}

// ── 그룹 → 공용 ShareHistoryRow 매핑 ─────────────────────────────
function toPubRecipient(r: ShareHistoryRecipient, g: ShareHistoryGroup): PubRecipient {
  return {
    recipientId: r.recipientId ?? undefined,
    historyId:   r.historyId,
    name:        r.name,
    role:        r.role,
    phone:       r.tel || null,
    channel:     r.channelType ?? null,
    viewed:      recipientViewed(r),
    viewedAt:    r.respAt,
    shareState:  shareStateOf(r),
    shareId:     r.shareId != null ? String(r.shareId) : null,
    // 일정공유의 고객 수신건만 발송자 미리보기(열람 기록 없이 새 창) — 반장·거래처 전용 뷰어 미리보기 경로는 없다.
    previewUrl:  g.dispatchType === 'SCHEDULE_SHARE' && r.recipientType === 'CUSTOMER' && r.shareId != null
      ? buildCustomerPreviewUrl(r.shareId, r.historyId)
      : null,
    resendable:  canResend(r),
  }
}

function toRow(g: ShareHistoryGroup): ShareHistoryRow {
  return {
    id:         g.groupKey,
    typeCode:   g.dispatchType,
    siteName:   g.siteName,
    sentAt:     g.sentDtm ?? '',
    expiresAt:  g.expiresDtm,
    recipients: g.recipients.map(r => toPubRecipient(r, g)),
    resendable: g.recipients.some(canResend),
    revokable:  g.recipients.some(canRevoke),
    raw:        g,
  }
}

type ImpactState =
  | { status: 'loading' }
  | { status: 'ready'; data: RevokeImpact }
  | { status: 'error' }

/** 서버 회수 영향 → 모달에 보여줄 안내 문장(정보 제공만, 취소를 막지 않는다) */
function impactLines(d: RevokeImpact): string[] {
  const lines: string[] = []
  if (!d.linkActive) lines.push('이미 취소되었거나 만료된 링크예요.')
  if (d.sameLinkHistoryCount > 1) lines.push(`같은 링크로 보낸 발송 ${d.sameLinkHistoryCount}건이 모두 열 수 없게 돼요.`)
  for (const m of d.otherMenus ?? []) {
    if (m.historyCount > 0) lines.push(`${SHARE_MENU_LABEL[m.menu] ?? m.menu}에서 같은 링크로 보낸 ${m.historyCount}건도 열 수 없게 돼요.`)
  }
  if (d.viewCount > 0) {
    lines.push(`이 링크는 ${d.viewCount}번 열렸어요${d.lastViewedDtm ? ` (마지막 ${fmtDateTime(d.lastViewedDtm)})` : ''}.`)
  }
  return lines
}


export function ShareHistoryView({ navigate }: Props) {
  const user = useAuthStore(s => s.user)
  const hdrs = {
    ...(user?.compId   ? { 'X-Comp-Id': String(user.compId)   } : {}),
    ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
  }

  // 알림톡 발송 가능 여부(서버 shareSendable, 종료일+7일) — 경과 현장의 일정공유 재발송만 비활성.
  // 조회 전·실패(null)는 막지 않는다(재발송 API 가 서버에서 다시 판정해 422 로 거부한다).
  const [sendableKey, setSendableKey] = useState(0)
  const sendableMap = useShareSendableMap(true, sendableKey)
  const isSendClosed = (g: ShareHistoryGroup): boolean =>
    g.dispatchType === 'SCHEDULE_SHARE' && sendableMap?.get(g.scheduleId) === false

  // 공용 목록의 재조회 트리거 — 재발송/회수 성공 후, 현재 조회조건·스크롤을 유지한 채
  // 첫 페이지부터 다시 불러온다. 공용 ShareHistoryScreen이 renderDetail에 넘겨주는
  // reload 핸들을 ref로 받아 모달·상세 바깥 핸들러에서도 호출한다(remount 불필요).
  const reloadRef = useRef<(() => void) | null>(null)
  // 재발송·회수 후 현재 조회조건·스크롤을 유지한 채 첫 페이지부터 다시 불러온다(reload).
  const requestReload = useCallback(() => {
    reloadRef.current?.()
  }, [])

  // ── 공용 어댑터: 서버 페이지네이션 ──
  // BE(52-Planner)가 그룹(발송 묶음) 단위로 페이징·필터(구분·열람상태·수신인)·정렬을 수행한다.
  // FE는 받은 페이지의 행을 그룹키로 재그룹핑만 하고(서버 순서 보존), 재정렬·클라 필터·슬라이싱을 하지 않는다.
  const fetchPage: FetchSharePage = useCallback(async (query, signal) => {
    const res = await apiClient.get<{ data: { items: RawShareHistory[]; totalCount: number; hasNext: boolean } }>(
      '/schedule/schedules/share-histories',
      {
        params: {
          sentFrom: ymdToDash(query.dateFrom),
          sentTo: ymdToDash(query.dateTo),
          dispatchType: query.typeCode || undefined,
          recipientName: query.keyword?.trim() || undefined,
          viewStatus: query.viewStatus || undefined,
          page: query.page,
          size: query.size,
          sort: query.sortDesc ? '-sentDtm' : 'sentDtm',
        },
        headers: hdrs,
        signal,
      },
    )
    const body = res.data.data
    const groups = groupHistories(body?.items ?? [])
    return { items: groups.map(toRow), hasNext: body?.hasNext ?? false }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.compId, user?.memberId])

  // ── 행 펼침 상세의 미리보기 수신인 선택(행별 — 선택한 이력 행 번호) ──────
  const [previewSel, setPreviewSel] = useState<Record<string, number>>({})

  // ── 재발송 모달 / 토스트 ─────────────────────────────────────
  type ResendTarget =
    | { kind: 'MOCK'; name: string; content: string }
    | { kind: 'SCHEDULE_SHARE'; scheduleId: number; historyId: number; name: string; content: string; expiresDtm: string | null; linkExpired: boolean }
    | { kind: 'FOREMAN_CONFIRM'; scheduleId: number; taskId: number; partnerId: number; name: string; mobile: string; recipientType: RecipientType; content: string }
  const [resendTarget, setResendTarget] = useState<ResendTarget | null>(null)
  const [resending, setResending] = useState(false)

  // ── 공유취소 확인 모달 ───────────────────────────────────────
  const [revokeTarget, setRevokeTarget] = useState<{ group: ShareHistoryGroup; rec: ShareHistoryRecipient } | null>(null)
  const [revoking, setRevoking] = useState(false)
  const [impact, setImpact] = useState<ImpactState>({ status: 'loading' })

  // 모달이 열리면 서버에서 회수 영향을 조회한다 — 정보 제공만, 조회가 실패해도 취소는 계속할 수 있다.
  useEffect(() => {
    if (!revokeTarget) return
    const controller = new AbortController()
    setImpact({ status: 'loading' })
    fetchRevokeImpact(revokeTarget.group.scheduleId, revokeTarget.rec.historyId, controller.signal)
      .then(data => setImpact({ status: 'ready', data }))
      .catch((err: { name?: string }) => {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return
        setImpact({ status: 'error' })
      })
    return () => controller.abort()
  }, [revokeTarget])

  // 일정공유(SCHEDULE_SHARE) 재발송은 서버가 이력 번호로 같은 링크·본문을 재사용해 처리한다.
  // 이력에 수신자 연락처가 없는 예전 데이터라면 여기서 바로 재발송할 수 없으므로, 그 경우에만
  // "스케줄 공유" 화면으로 이동시킨다.
  // 시공반장 전송(FOREMAN_CONFIRM)은 taskId가 있어야 확인/불가능 버튼 링크를 다시 구성할 수
  // 있다 — 옛 이력이라 taskId가 없으면(또는 연락처가 없으면) 재발송을 할 수 없다.
  const handleResend = (row: ShareHistoryGroup, rec: ShareHistoryRecipient) => {
    if (row.dispatchType === 'SCHEDULE_SHARE') {
      if (isSendClosed(row)) { toast.error(SEND_WINDOW_CLOSED_NOTICE); return }
      if (!rec.tel.trim()) { navigate('kakao', row.scheduleId); return }
      setResendTarget({
        kind: 'SCHEDULE_SHARE', scheduleId: row.scheduleId, historyId: rec.historyId, name: rec.name,
        content: rec.content, expiresDtm: rec.expiresDtm, linkExpired: isPastExpiry(rec.expiresDtm),
      })
      return
    }
    if (row.dispatchType === 'FOREMAN_CONFIRM') {
      if (!rec.tel.trim() || rec.taskId == null || rec.recipientId == null) {
        toast.error('연락처 또는 공종 정보가 없어 재발송할 수 없습니다.')
        return
      }
      setResendTarget({ kind: 'FOREMAN_CONFIRM', scheduleId: row.scheduleId, taskId: rec.taskId, partnerId: rec.recipientId, name: rec.name, mobile: rec.tel, recipientType: rec.recipientType, content: rec.content })
      return
    }
    setResendTarget({ kind: 'MOCK', name: rec.name, content: row.content })
  }

  // 공유취소 — 이미 보낸 공유 링크를 회수한다. 영향을 보여 주는 확인 모달을 거쳐 실행하고,
  // 성공 시 공용 목록을 서버 기준으로 다시 불러온다.
  const closeRevoke = () => { if (!revoking) setRevokeTarget(null) }
  const confirmRevoke = async () => {
    const target = revokeTarget
    if (!target || revoking) return
    setRevoking(true)
    try {
      await apiClient.post(`/schedule/schedules/${target.group.scheduleId}/share-history/${target.rec.historyId}/revoke`, {}, { headers: hdrs })
      toast.success('공유를 취소했습니다.')
      setRevokeTarget(null)
      requestReload()
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 409) {
        toast.error('이미 취소된 링크입니다.')
        setRevokeTarget(null)
        requestReload()
      } else {
        toast.error('공유 취소 중 오류가 발생했습니다.')
      }
    } finally {
      setRevoking(false)
    }
  }

  const closeResend = () => setResendTarget(null)
  const confirmResend = async () => {
    const target = resendTarget
    if (!target) return
    if (target.kind === 'MOCK') {
      closeResend()
      toast.success('재발송 알림톡을 발송했습니다')
      return
    }
    if (target.kind === 'FOREMAN_CONFIRM') {
      setResending(true)
      try {
        // 서버가 링크 발급·알림톡 발송·이력 기록·상태 전이를 모두 수행한다 — RESEND(공종·반장별 1번) — 누른 행의 수신 반장을 partnerId 로 보내며, 서버가 현재 반장과 같을 때만 발송한다.
        await scheduleApi.sendForemanConfirm(target.taskId, { sendMethod: 'RESEND', partnerId: target.partnerId })
        closeResend()
        toast.success('일정확인 카톡을 다시 보냈어요')
      } catch (err) {
        toast.error(foremanSendErrorMessage(err))
      } finally {
        requestReload()
        setResending(false)
      }
      return
    }
    // 일정공유 — 서버 재발송(같은 링크·새 이력 행·본문·이력은 서버가 처리). 화면은 문구를 만들지 않는다.
    setResending(true)
    try {
      const result = await resendScheduleShareHistory(target.scheduleId, target.historyId)
      const failed = result.failedCount ?? 0
      const sent = result.sentCount ?? (failed > 0 ? 0 : 1)
      if (sent === 0 && failed > 0) {
        toast.error('재발송에 실패했어요. 잠시 후 다시 시도해 주세요.')
      } else {
        closeResend()
        toast.success('재발송 알림톡을 발송했습니다')
      }
    } catch (err) {
      const info = apiErrorInfo(err)
      if (info.code === SHARE_SEND_WINDOW_CLOSED) {
        // 서버가 발송 불가로 판정 — 안내하고 발송 가능 여부를 다시 읽어 버튼을 비활성으로 맞춘다.
        closeResend()
        toast.error(SEND_WINDOW_CLOSED_NOTICE)
        setSendableKey(k => k + 1)
      } else {
        toast.error(info.message || '재발송 중 오류가 발생했어요.')
      }
    } finally {
      requestReload()
      setResending(false)
    }
  }

  // ── 행 펼침 상세 — 발송 원문 미리보기 + 수신인별 상태·재발송·공유취소 ──
  const renderDetail = (row: ShareHistoryRow, { reload }: { reload: () => void }) => {
    // 현재 조회조건·스크롤을 유지한 채 재조회하는 공용 핸들 — 모달 확인(confirmResend) 등
    // 상세 바깥에서 호출하는 핸들러도 쓸 수 있도록 ref에 최신 값을 보관한다.
    reloadRef.current = reload
    const group = row.raw as ShareHistoryGroup
    const selected = group.recipients.find(r => r.historyId === previewSel[row.id]) ?? group.recipients[0]
    const sentDtmLabel = fmtDateTime(group.sentDtm)
    const sendClosed = isSendClosed(group)
    return (
      <div style={{ padding: '16px 20px 18px', display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap', background: 'var(--shell-bg)' }}>
        <div style={{ width: group.dispatchType === 'SCHEDULE_SHARE' ? 300 : 360, flexShrink: 0 }}>
          <div style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)', marginBottom: 8 }}>전송 내역</div>
          {/* 발송 당시 서버가 저장한 원문(msgContent)을 실제 알림톡과 동일한 카드에 얹는다 — 화면이 문구를 다시 조립하지 않는다.
              group.content(그룹 대표값)는 msgTitle을 우선하므로(목록 요약용) 수신인 본문을 써야 실제 발송 내용이 보인다. */}
          <KakaoMessageCard
            content={selected?.content ?? group.content}
            buttons={group.dispatchType === 'SCHEDULE_SHARE' ? ['공유 일정 확인하기'] : ['★ 일정가능 ★', '일정불가능']}
          />
          <div style={{ fontSize: 10.5, color: 'var(--shell-hint)', marginTop: 6 }}>발송시각 {sentDtmLabel}</div>
        </div>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)', marginBottom: 8 }}>수신인 {group.recipients.length}명</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {group.recipients.map(rec => {
              const canResendRec = canResend(rec)
              const avatarBg = ROLE_COLOR[rec.role] ?? 'var(--shell-on-surface-2)'
              const selectable = group.dispatchType === 'SCHEDULE_SHARE' && group.recipients.length > 1
              const isPreviewed = selectable && selected?.historyId === rec.historyId
              return (
                <div
                  key={rec.historyId}
                  role={selectable ? 'button' : undefined}
                  tabIndex={selectable ? 0 : undefined}
                  onClick={() => selectable && setPreviewSel(p => ({ ...p, [row.id]: rec.historyId }))}
                  onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && selectable) { e.preventDefault(); setPreviewSel(p => ({ ...p, [row.id]: rec.historyId })) } }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 12,
                    background: isPreviewed ? 'var(--gt-subtle)' : 'var(--shell-surface)',
                    border: `var(--border-width-hairline) solid ${isPreviewed ? 'var(--gt-primary)' : 'var(--shell-border)'}`,
                    cursor: selectable ? 'pointer' : 'default',
                  }}
                >
                  <div style={{ width: 30, height: 30, borderRadius: '50%', background: avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--shell-surface)', flexShrink: 0 }}>{rec.name[0]}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface)', whiteSpace: 'nowrap' }}>{rec.name} <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--shell-hint)' }}>· {rec.role}</span></div>
                  </div>
                  <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <StatusBadge rec={rec} />
                    <ShareStateBadge state={shareStateOf(rec)} />
                  </div>
                  <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)', minWidth: 96, textAlign: 'right', flexShrink: 0 }}>{rec.respAt ? fmtDateTime(rec.respAt) : '—'}</div>
                  {/* 재발송·공유취소 고정폭 2열 — 한쪽이 빠져도 다른 쪽 버튼 위치가 수신인마다 밀리지 않도록 한다. */}
                  <div style={{ width: 176, flexShrink: 0, display: 'grid', gridTemplateColumns: '84px 84px', columnGap: 6, justifyItems: 'center' }}>
                    <div>
                      {canResendRec && (() => {
                        // 종료일+7일 경과 — 알림톡이라 재발송만 비활성(링크 복사는 스케줄 공유 화면에서 가능)
                        const resendBtn = (
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={sendClosed}
                            className="!h-auto gap-[5px] !px-3 !py-[6px] text-[length:var(--type-caption-size)] whitespace-nowrap"
                            onClick={e => { e.stopPropagation(); handleResend(group, rec) }}
                          >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                            재발송
                          </Button>
                        )
                        return sendClosed ? <Tooltip tip={SEND_WINDOW_CLOSED_NOTICE}>{resendBtn}</Tooltip> : resendBtn
                      })()}
                    </div>
                    <div>
                      {canRevoke(rec) && (
                        <Button
                          variant="secondary"
                          size="sm"
                          className="!h-auto !px-3 !py-[6px] text-[length:var(--type-caption-size)] whitespace-nowrap !text-danger-600 !border-danger"
                          onClick={e => { e.stopPropagation(); setRevokeTarget({ group, rec }) }}
                        >
                          공유취소
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  const impactHints = impact.status === 'ready' ? impactLines(impact.data) : []
  const resendExpiryMd = resendTarget?.kind === 'SCHEDULE_SHARE' ? monthDay(resendTarget.expiresDtm) : ''

  return (
    <>
      <ShareHistoryScreen
        title="스케줄 공유 이력"
        description="시공반장에게 보낸 작업일정 알림톡과 고객·거래처에게 보낸 일정공유 내역의 열람 여부를 확인하고, 필요 시 재발송할 수 있습니다."
        typeOptions={TYPE_OPTIONS}
        typeAllLabel="전체 구분"
        keywordPlaceholder="수신인 검색"
        fetchPage={fetchPage}
        renderDetail={renderDetail}
      />

      {/* 공유취소 확인 모달 — 서버가 알려주는 영향을 보여 주되 취소를 막지 않는다 */}
      <Modal
        open={revokeTarget != null}
        onClose={closeRevoke}
        title="공유취소"
        footerAlign="stretch"
        footer={
          <>
            <Button variant="secondary" disabled={revoking} onClick={closeRevoke}>닫기</Button>
            <Button variant="danger" loading={revoking} onClick={() => void confirmRevoke()}>공유취소</Button>
          </>
        }
      >
        {revokeTarget && (
          <div className="flex flex-col">
            <p className="text-body-sm text-text">
              <b>{revokeTarget.rec.name}</b>님께 보낸 링크를 취소하면 받은 분은 더 이상 열 수 없어요.
            </p>
            <div className="mt-1 flex flex-col gap-1">
              {impact.status === 'loading' && <StatusHint tone="muted">영향 범위를 확인하고 있어요.</StatusHint>}
              {impact.status === 'error' && <StatusHint tone="muted">영향 범위를 불러오지 못했어요. 취소는 계속할 수 있어요.</StatusHint>}
              {impactHints.map(t => <StatusHint key={t} tone="warning">{t}</StatusHint>)}
            </div>
          </div>
        )}
      </Modal>

      {/* 재발송 확인 모달 */}
      <Modal
        open={resendTarget != null}
        onClose={closeResend}
        title="재발송 확인"
        footerAlign="stretch"
        footer={resendTarget && (resendTarget.kind === 'SCHEDULE_SHARE' ? (
          <>
            <Button variant="secondary" disabled={resending} onClick={closeResend}>취소</Button>
            <Button
              variant="primary"
              loading={resending}
              onClick={() => void confirmResend()}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              {resending ? '발송 중...' : '재발송하기'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" disabled={resending} onClick={closeResend}>취소</Button>
            <Button variant="primary" onClick={() => void confirmResend()} disabled={resending} style={{ background: 'var(--brand-kakao-container)', borderColor: 'var(--brand-kakao-container)', color: 'var(--brand-kakao-label)' }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              {resending ? '발송 중...' : '재발송하기'}
            </Button>
          </>
        ))}
      >
        {resendTarget && (resendTarget.kind === 'SCHEDULE_SHARE' ? (
          <>
            {/* 처음 발송할 때 서버가 저장한 문구 그대로 보여 준다 — 유효기간은 서버가 산정하므로 입력 칸이 없다 */}
            <KakaoMessageCard content={resendTarget.content} buttons={['공유 일정 확인하기']} />
            <div className="mt-1 flex flex-col gap-1">
              <StatusHint tone="info">
                {`기존 링크로 다시 보내요${resendExpiryMd ? `. 링크는 ${resendExpiryMd}까지 열려요` : ''}.`}
              </StatusHint>
              {resendTarget.linkExpired && (
                <StatusHint tone="warning">이전 링크는 만료되어 새 링크로 보내요.</StatusHint>
              )}
            </div>
          </>
        ) : (
          <>
            {/* 카카오 알림톡 예시(목업) — 실제 카카오톡 UI 재현, 브랜드 컬러 유지 */}
            <div style={{ background: 'var(--brand-kakao-container)', borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--elevation-2)' }}>
              <div style={{ background: 'var(--brand-kakao-symbol)', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 9 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--brand-kakao-container)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 13 }}>💬</div>
                <div>
                  <div style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--brand-kakao-container)' }}>카카오 알림톡</div>
                  <div style={{ fontSize: 10, color: 'var(--alpha-white-600)', marginTop: 1 }}>인디스케줄러</div>
                </div>
              </div>
              <div style={{ padding: 14, background: 'var(--shell-surface)' }}>
                <div style={{ fontWeight: 700, color: 'var(--shell-on-surface)', fontSize: 12, marginBottom: 8 }}>{resendTarget.name} 님께</div>
                <div style={{ background: 'var(--shell-bg)', borderRadius: 12, padding: '10px 12px', fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', lineHeight: 1.6 }}>
                  <div>{resendTarget.content}</div>
                </div>
              </div>
            </div>
            <div style={{ marginTop: 12, fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', lineHeight: 1.6 }}>
              위 내용 그대로 <b style={{ color: 'var(--shell-on-surface-2)' }}>{resendTarget.name}</b> 님께 카카오 알림톡을 다시 발송합니다.
            </div>
          </>
        ))}
      </Modal>
    </>
  )
}
